import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowDown,
  ArrowUp,
  Loader2,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  useCampaignDispositions,
  useCreateDisposition,
  useDeleteDisposition,
  useOverrideCampaignDispositions,
  useResetCampaignDispositions,
  useUpdateDisposition,
} from '@/api/powerDialer'
import type { Disposition, DispositionCategory } from '@/types/powerDialer'
import {
  CATEGORY_OPTIONS,
  styleForCategory,
} from '@/lib/dispositionCategory'
import { CompanyDispositionsDialog } from './CompanyDispositionsDialog'

interface Props {
  campaignId: number | null // null en mode "création" (avant que la campagne existe)
}

export function CampaignDispositionsSection({ campaignId }: Props) {
  const { t } = useTranslation()
  const query = useCampaignDispositions(campaignId)
  const overrideMut = useOverrideCampaignDispositions(campaignId ?? 0)
  const resetMut = useResetCampaignDispositions(campaignId ?? 0)
  const createMut = useCreateDisposition()
  const updateMut = useUpdateDisposition()
  const deleteMut = useDeleteDisposition()

  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [newCategory, setNewCategory] = useState<DispositionCategory>('neutral')
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false)

  const dispositions = query.data?.dispositions ?? []
  const scope = query.data?.scope ?? 'company'
  const isOverride = scope === 'campaign'

  // Mode édition d'une disposition (inline)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')

  const handleToggleOverride = async (checked: boolean) => {
    if (!campaignId) return
    try {
      if (checked && !isOverride) await overrideMut.mutateAsync()
      if (!checked && isOverride) await resetMut.mutateAsync()
    } catch {
      // toast géré ailleurs
    }
  }

  const handleAdd = async () => {
    if (!campaignId || !newName.trim() || !isOverride) return
    const slug = newName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
    try {
      await createMut.mutateAsync({
        slug,
        name: newName.trim(),
        category: newCategory,
        campaign: campaignId,
        order: (dispositions[dispositions.length - 1]?.order ?? 0) + 10,
      })
      // L'invalidation est gérée par le hook mais notre query est scopée campaign,
      // on relance manuellement le refetch effectif.
      void query.refetch()
      setNewName('')
      setNewCategory('neutral')
      setAdding(false)
    } catch {
      // toast géré ailleurs
    }
  }

  const handlePatch = async (d: Disposition, patch: Partial<Disposition>) => {
    try {
      await updateMut.mutateAsync({ id: d.id, patch })
      void query.refetch()
    } catch {
      // toast géré ailleurs
    }
  }

  const handleDelete = async (d: Disposition) => {
    try {
      await deleteMut.mutateAsync(d.id)
      void query.refetch()
    } catch {
      // toast géré ailleurs
    }
  }

  // Swap d'order entre deux dispositions adjacentes pour reorder up/down.
  // On évite le drag-and-drop pour ne pas ajouter de dépendance.
  const handleMove = async (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= dispositions.length) return
    const current = dispositions[index]
    const other = dispositions[targetIndex]
    try {
      await Promise.all([
        updateMut.mutateAsync({ id: current.id, patch: { order: other.order } }),
        updateMut.mutateAsync({ id: other.id, patch: { order: current.order } }),
      ])
      void query.refetch()
    } catch {
      // toast géré ailleurs
    }
  }

  if (!campaignId) {
    // En mode création de campagne : on n'a pas encore d'ID, donc impossible
    // de customiser. L'agent customisera après création via "Modifier la campagne".
    return (
      <div className="rounded-lg border border-dashed border-slate-200 dark:border-slate-700 p-3">
        <Label className="text-sm font-medium block mb-1">
          {t('manager.powerDialer.dispositions.title')}
        </Label>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t('manager.powerDialer.dispositions.createHint')}
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <Label className="text-sm font-medium block">
            {t('manager.powerDialer.dispositions.customTitle')}
          </Label>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {isOverride
              ? t('manager.powerDialer.dispositions.campaignOwn')
              : t('manager.powerDialer.dispositions.campaignDefault')}
          </p>
          {!isOverride && (
            <button
              type="button"
              onClick={() => setCompanyDialogOpen(true)}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1"
            >
              {t('manager.powerDialer.dispositions.editCompanyDefaults')}
            </button>
          )}
        </div>
        <Switch
          checked={isOverride}
          disabled={overrideMut.isPending || resetMut.isPending}
          onCheckedChange={(checked) => void handleToggleOverride(checked)}
        />
      </div>

      <CompanyDispositionsDialog
        open={companyDialogOpen}
        onOpenChange={setCompanyDialogOpen}
      />

      {query.isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-1.5">
          {dispositions.map((d, index) => {
            const catStyle = styleForCategory(d.category)
            const Icon = catStyle.icon
            const isEditing = editingId === d.id
            return (
              <div
                key={d.id}
                className={cn(
                  'flex items-center gap-2 rounded-md border px-2 py-1.5',
                  'border-slate-200 dark:border-slate-700',
                  !isOverride && 'opacity-60',
                )}
              >
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    disabled={!isOverride || index === 0}
                    onClick={() => void handleMove(index, -1)}
                    className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed leading-none"
                    title={t('manager.powerDialer.dispositions.moveUp')}
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    disabled={!isOverride || index === dispositions.length - 1}
                    onClick={() => void handleMove(index, 1)}
                    className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed leading-none"
                    title={t('manager.powerDialer.dispositions.moveDown')}
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>
                <span
                  className={cn(
                    'w-5 h-5 rounded flex items-center justify-center shrink-0 border',
                    catStyle.badgeLight,
                    'dark:hidden',
                  )}
                >
                  <Icon className="w-3 h-3" />
                </span>
                <span
                  className={cn(
                    'w-5 h-5 rounded items-center justify-center shrink-0 border hidden dark:flex',
                    catStyle.badgeDark,
                  )}
                >
                  <Icon className="w-3 h-3" />
                </span>
                {isEditing ? (
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={() => {
                      if (editName.trim() && editName !== d.name) {
                        void handlePatch(d, { name: editName.trim() })
                      }
                      setEditingId(null)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur()
                      if (e.key === 'Escape') setEditingId(null)
                    }}
                    autoFocus
                    className="h-7 text-xs flex-1"
                  />
                ) : (
                  <button
                    type="button"
                    disabled={!isOverride}
                    onClick={() => {
                      setEditingId(d.id)
                      setEditName(d.name)
                    }}
                    className="flex-1 text-left text-sm text-slate-900 dark:text-white truncate disabled:cursor-not-allowed"
                  >
                    {d.name}
                  </button>
                )}

                <select
                  disabled={!isOverride}
                  value={d.category}
                  onChange={(e) =>
                    void handlePatch(d, {
                      category: e.target.value as DispositionCategory,
                    })
                  }
                  title={t('manager.powerDialer.dispositions.categoryTitle')}
                  className="text-[11px] bg-transparent border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5 disabled:cursor-not-allowed max-w-[9rem]"
                >
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {t(`manager.powerDialer.dispositions.categoryOptions.${c.value}`, c.label)}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  disabled={!isOverride}
                  onClick={() => void handleDelete(d)}
                  className="p-1 text-slate-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                  title={t('manager.powerDialer.dispositions.delete')}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )
          })}
        </div>
      )}

      {isOverride && (
        <>
          {adding ? (
            <div className="flex items-center gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t('manager.powerDialer.dispositions.namePlaceholder')}
                className="h-8 text-xs flex-1"
                autoFocus
              />
              <select
                value={newCategory}
                onChange={(e) =>
                  setNewCategory(e.target.value as DispositionCategory)
                }
                className="text-xs bg-transparent border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5"
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {t(`manager.powerDialer.dispositions.categoryOptions.${c.value}`, c.label)}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                size="sm"
                onClick={() => void handleAdd()}
                disabled={!newName.trim() || createMut.isPending}
              >
                {createMut.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  t('manager.powerDialer.dispositions.add')
                )}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setAdding(false)
                  setNewName('')
                }}
              >
                {t('manager.powerDialer.dispositions.cancel')}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAdding(true)}
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                {t('manager.powerDialer.dispositions.addDisposition')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void resetMut.mutateAsync().then(() => query.refetch())}
                disabled={resetMut.isPending}
                className="text-xs text-slate-500"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                {t('manager.powerDialer.dispositions.resetDefaults')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
